import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CityMapComponent } from '@/new-transport/_shared/city-map/city-map.component';
import { TransportMapStore } from '@/new-transport/transport-map/_store/transport-map.store';
import { MapDestinationComponent } from '@/new-transport/_shared/map-destination/map-destination.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TopBarComponent } from '@/new-transport/_shared/topbar/top-bar.component';
import {
	StopKind,
	TransportAction,
	TransportKind,
	TransportModule,
	TransportSide,
} from '@/new-transport/_models/transport.model';
import { distinctUntilChanged, filter, finalize, map, Observable, of, pipe, switchMap, take, tap } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { VehiclesSidenavComponent } from '@/new-transport/_shared/vehicles-sidenav/vehicles-sidenav.component';
import { VehicleSidenavHandleService } from '@/new-transport/_shared/vehicles-sidenav/vehicle-sidenav-handle.service';
import { VehicleInUseComponent } from '@/new-transport/_shared/vehicle-in-use/vehicle-in-use.component';
import { CustomMatLoaderComponent } from '@/common/custom-mat-loader/custom-mat-loader.component';
import { NewUndoRedoService } from '@/common/undo-redo/new-undo-redo.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { deepClone } from '@/_utils/deep-clone';
import { getState } from '@ngrx/signals';
import { TransportMapTimelineComponent } from '@/new-transport/transport-map/transport-map-timeline/transport-map-timeline.component';
import {
	SaveTransportMapPayload,
	TransportLogic,
	TransportMapApiService,
	UpdateTransportMapPayload,
} from '@/new-transport/transport-map/transport-map.api.service';
import { QuestionService } from '@/common/question-dialog/question-dialog.service';
import { TransportMapValidator } from '@/new-transport/transport-map/transport-map-validator/transport-map.validator';
import { LoaderService } from '@/common/loader/loader.service';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';

@Component({
	selector: 'app-transport-map',
	standalone: true,
	imports: [
		CityMapComponent,
		MapDestinationComponent,
		TopBarComponent,
		MatInputModule,
		MatFormFieldModule,
		MatSidenavModule,
		VehiclesSidenavComponent,
		CustomMatLoaderComponent,
		VehicleInUseComponent,
		TransportMapTimelineComponent,
		TopBarComponent,
	],
	providers: [TransportMapStore],
	templateUrl: './transport-map.component.html',
	styleUrl: './transport-map.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportMapComponent implements OnInit {
	readonly TransportAction = TransportAction;
	readonly TransportModule = TransportModule;
	readonly TransportKind = TransportKind;

	readonly store = inject(TransportMapStore);

	private readonly vehiclesSidenavHandle = inject(VehicleSidenavHandleService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly destroyRef = inject(DestroyRef);
	private readonly undoRedoService = inject(NewUndoRedoService);
	private readonly question = inject(QuestionService);
	private readonly router = inject(Router);
	private readonly apiService = inject(TransportMapApiService);
	private readonly loader = inject(LoaderService);
	private readonly infobox = inject(InfoboxService);

	private readonly transportKind = toSignal(
		this.activatedRoute.queryParams.pipe(
			map((params) => params?.transportKind ?? OfferTransportKind.COACH),
			distinctUntilChanged(),
		),
	);

	private readonly setInitialUndoRedoState = rxMethod<boolean>(
		pipe(
			filter((element) => !element),
			take(1),
			tap(() => this.undoRedoService.setState(deepClone(getState(this.store)))),
		),
	);

	ngOnInit(): void {
		this.setInitialUndoRedoState(this.store.someRequestIsLoading);
	}

	toggleSidenav(): void {
		this.vehiclesSidenavHandle.toggle();
	}

	triggerHistoryAction(action: 'undo' | 'redo'): void {
		this.undoRedoService[action]();
		const currentState = this.undoRedoService.getState();
		this.store.patchWholeState(currentState);
	}

	changeTransportName($event: string): void {
		this.store.changeTransportName($event);
	}

	saveTransportMap(): void {
		this.validateTransport();

		this.saveNewTransport(this.store.transportAction());
	}

	includeDestinationInRoute($event: number): void {
		this.store.setVehicleRoute({
			destinationId: $event,
			cityId: $event,
			vehicleId: this.store.vehicleInUse().id,
			action: StopKind.TARGET,
			fromCity: $event,
			slots: [],
		});

		this.infobox.success('Pomyślnie dodano destynację do trasy!');
	}

	private showRedirectToTimetableQuestion(transportId: number): void {
		this.question
			.ask('Transport i rozkład jazdy zostały zapisane pomyślnie! Czy chcesz przejść do rozkładu jazdy?')
			.subscribe((redirect) => {
				if (redirect.result) {
					void this.router.navigate([`transport/${transportId}/table/new`], {
						queryParams: this.activatedRoute.snapshot.queryParams,
					});
				} else if (this.store.transportAction() === TransportAction.ADD) {
					// redirect user to freshly created transport
					void this.router.navigate([`/transport/${transportId}/map/new`], {
						queryParams: this.activatedRoute.snapshot.queryParams,
					});
				} else {
					// reload page to clear state
					window.location.reload();
				}
			});
	}

	private linkTransportsIfNeeded(transportId: number): Observable<number> {
		if (!this.store.transportsToLink().length) {
			return of(transportId);
		}

		const transportsToLink = this.store.transportsToLink().map((transport) => transport.id);

		return this.apiService.linkTransports(transportsToLink, transportId).pipe(map(() => transportId));
	}

	private mapVehicleRouteToTransportLogic(): TransportLogic[] {
		const logics: TransportLogic[] = [];

		Array.from(this.store.routeForVehicles().entries()).map(([vehicleId, route]) => {
			const transportLogics: TransportLogic[] = route.map((stop, index) => {
				return {
					action: stop.action,
					city: stop.cityId,
					occupiedSpace: stop.slots.length,
					order: index + 1,
					originCity: stop.fromCity,
					vehicle: vehicleId,
					stopDestination: stop.destinationId,
					reservationOfferSlots: stop.slots.map((slot) => slot.id),
				};
			});
			logics.push(...transportLogics);
		});

		return logics;
	}

	private getTransportMapPayload(action: TransportAction): SaveTransportMapPayload | UpdateTransportMapPayload {
		const transportMapPayload: UpdateTransportMapPayload | SaveTransportMapPayload = {
			id: this.store.transport().id,
			side: this.store.transport().side,
			kind: this.store.transport().kind || TransportKind.OFFER,
			name: this.store.transport().name,
			linked: this.store.isLinkedTransport(),
			transportlogicSet: this.mapVehicleRouteToTransportLogic(),
			transportsToLink: [],
			departureTerms: [],
			returnTerms: [],
		};

		if (action === TransportAction.EDIT) {
			transportMapPayload.id = this.store.transport().id;
		}

		if (action === TransportAction.ADD) {
			transportMapPayload.transportsToLink = this.store.transportsToLink().map((transport) => transport.id);
		}

		const transportTermProperty =
			this.store.transport().side === TransportSide.DEPARTURE ? 'departureTerms' : 'returnTerms';

		transportMapPayload[transportTermProperty] = removeDuplicatesFromArray(
			this.store
				.terms()
				.map((term) => term.id)
				.concat(this.store.transportsToLinkTerms().map((term) => term.id))
				.filter(Boolean),
		);

		return transportMapPayload;
	}

	private validateTransport(): void {
		const { transport, routeForVehicles, citiesWithPrograms, destinations, takenPrograms, vehicles } = this.store;

		const validator = new TransportMapValidator(
			transport()?.name,
			routeForVehicles(),
			citiesWithPrograms(),
			destinations(),
			takenPrograms(),
			vehicles(),
			this.transportKind(),
		);

		validator.validate();
	}

	private saveNewTransport(action: TransportAction): void {
		const payload = this.getTransportMapPayload(action);
		let request$: Observable<number>;

		switch (action) {
			case TransportAction.ADD:
				request$ = this.apiService.saveTransportMap(payload as SaveTransportMapPayload);
				break;
			case TransportAction.EDIT:
				request$ = this.apiService.updateTransportMap(payload as UpdateTransportMapPayload);
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const value: never = action;
				throw new Error(`There is no value like: ${value}`);
		}

		this.loader.show();

		request$
			.pipe(
				switchMap((id) => this.linkTransportsIfNeeded(id)),
				finalize(() => this.loader.hide()),
			)
			.subscribe((id: number) => this.showRedirectToTimetableQuestion(id));
	}
}
